(() => {
  if (window.__lounelleCartUpsellDialogV1) return;
  window.__lounelleCartUpsellDialogV1 = true;

  const rootPath = () => window.Shopify?.routes?.root || '/';
  const desktopMedia = window.matchMedia('(min-width: 768px)');
  const resources = new Map();
  let dialog;
  let content;
  let status;
  let closeButton;
  let session = null;
  let opening = false;
  let sizeGuideCleanup = null;
  let zoomCleanup = null;

  function loadResource(url, type) {
    if (resources.has(url)) return resources.get(url);
    const promise = new Promise((resolve, reject) => {
      const element = document.createElement(type === 'style' ? 'link' : 'script');
      const timer = setTimeout(() => {
        element.remove();
        resources.delete(url);
        reject(new Error('Resource loading timed out'));
      }, 15000);
      element.onload = () => { clearTimeout(timer); resolve(); };
      element.onerror = () => {
        clearTimeout(timer);
        element.remove();
        resources.delete(url);
        reject(new Error('Resource loading failed'));
      };
      if (type === 'style') {
        element.rel = 'stylesheet';
        element.href = url;
      } else {
        element.src = url;
        element.async = true;
      }
      document.head.appendChild(element);
    });
    resources.set(url, promise);
    return promise;
  }

  function showError(message) {
    status.textContent = message || session?.errorLabel || '';
    status.hidden = false;
  }

  function disposeProduct() {
    zoomCleanup?.();
    zoomCleanup = null;
    sizeGuideCleanup?.();
    sizeGuideCleanup = null;
    content.querySelectorAll('product-info').forEach((info) => {
      info.abortController?.abort();
      info.flkty?.destroy();
      info.flktyNav?.destroy();
      info.flkty = null;
      info.flktyNav = null;
    });
    content.replaceChildren();
  }

  function closeModal() {
    if (!session || session.adding) return;
    const nested = content.querySelector('drawer-element.is-open');
    if (nested) { nested.close?.(true); return; }
    dialog.close();
  }

  function restoreCart() {
    const previous = session;
    session = null;
    if (!previous) return;
    previous.controller?.abort();
    disposeProduct();
    previous.trigger.removeAttribute('aria-busy');
    previous.trigger.classList.remove('is-loading');
    document.documentElement.classList.remove('lounelle-upsell-dialog-open');
    const cart = previous.cart;
    if (cart.isConnected && cart.isCartDrawerOpen) {
      const target = previous.trigger.isConnected
        ? previous.trigger
        : cart.querySelector('[name="checkout"], [data-cart-drawer-toggle]');
      window.theme?.a11y?.trapFocus({ container: cart, elementToFocus: target || cart });
      target?.focus({ preventScroll: true });
      previous.scroll.forEach(([node, top, left]) => {
        if (node.isConnected) { node.scrollTop = top; node.scrollLeft = left; }
      });
    }
  }

  function ensureDialog(labels) {
    if (!dialog) {
      dialog = document.createElement('dialog');
      dialog.className = 'lounelle-upsell-dialog';
      dialog.dataset.lounelleUpsellDialog = '';
      dialog.setAttribute('aria-modal', 'true');
      closeButton = document.createElement('button');
      closeButton.type = 'button';
      closeButton.className = 'lounelle-upsell-dialog__close';
      closeButton.innerHTML = '<svg viewBox="0 0 24 24" width="20" height="20" aria-hidden="true"><path d="m6 6 12 12M18 6 6 18" fill="none" stroke="currentColor" stroke-width="1.6"/></svg>';
      closeButton.addEventListener('click', closeModal);
      status = document.createElement('p');
      status.className = 'lounelle-upsell-dialog__status';
      status.setAttribute('role', 'alert');
      content = document.createElement('div');
      content.className = 'lounelle-upsell-dialog__content';
      dialog.append(closeButton, status, content);
      document.body.appendChild(dialog);
      dialog.addEventListener('cancel', (event) => { event.preventDefault(); closeModal(); });
      dialog.addEventListener('close', restoreCart);
      dialog.addEventListener('mousedown', (event) => event.stopPropagation());
      dialog.addEventListener('keyup', (event) => event.stopPropagation());
      dialog.addEventListener('click', (event) => {
        event.stopPropagation();
        const target = event.target instanceof Element ? event.target : null;
        if (!target) return;
        if (target.closest('[data-popup-close]')) {
          event.preventDefault();
          closeModal();
          return;
        }
        const sibling = target.closest('[data-sibling-handle]');
        if (sibling) {
          event.preventDefault();
          if (!session?.adding) loadProduct(`${rootPath()}products/${encodeURIComponent(sibling.dataset.siblingHandle)}`);
          return;
        }
        if (target === dialog) {
          const box = dialog.getBoundingClientRect();
          if (event.clientX < box.left || event.clientX > box.right || event.clientY < box.top || event.clientY > box.bottom) closeModal();
        }
      });
      dialog.addEventListener('submit', addProduct, true);
    }
    closeButton.setAttribute('aria-label', labels.closeLabel);
    closeButton.disabled = false;
    dialog.setAttribute('aria-label', labels.title);
    status.hidden = true;
    status.textContent = '';
  }

  async function loadProduct(productUrl) {
    const current = session;
    if (!current) return;
    current.controller?.abort();
    const controller = new AbortController();
    current.controller = controller;
    current.loading = true;
    const timer = setTimeout(() => controller.abort(), 20000);
    dialog.setAttribute('aria-busy', 'true');
    content.inert = true;
    status.hidden = true;
    try {
      const url = new URL(productUrl, location.origin);
      if (url.origin !== location.origin) throw new Error('Invalid product origin');
      url.search = '';
      url.searchParams.set('section_id', 'api-cart-upsell');
      const response = await fetch(url, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error(`Product loading failed (${response.status})`);
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      if (!customElements.get('product-info')) {
        const script = Array.from(html.querySelectorAll('script[src]')).find((node) => new URL(node.getAttribute('src'), location.href).pathname.endsWith('/product-info.js'));
        if (!script) throw new Error('Product controls are unavailable');
        await loadResource(new URL(script.getAttribute('src'), location.href).href, 'script');
      }
      const sizeConfigNode = html.querySelector('[data-lounelle-upsell-size-config]');
      const sizeConfig = sizeConfigNode ? JSON.parse(sizeConfigNode.textContent) : null;
      if (sizeConfig?.script) await loadResource(sizeConfig.script, 'script');
      const inner = html.querySelector('[data-quick-view-inner]');
      if (!inner || !inner.querySelector('product-info form[data-product-form]')) throw new Error('Product form is unavailable');
      inner.querySelectorAll('[data-quick-view-body] > p, .product__description, .popup-quick-view__view-button, .popup-quick-view__close, script:not([type="application/json"])').forEach((node) => node.remove());
      const title = inner.querySelector('.popup-quick-view__title');
      const titleLink = title?.querySelector('a');
      if (titleLink) titleLink.replaceWith(...titleLink.childNodes);
      if (session !== current || controller.signal.aborted) return;
      disposeProduct();
      if (sizeConfig && window.LounelleCartUpsellSizeGuide) {
        sizeGuideCleanup = window.LounelleCartUpsellSizeGuide.mount(inner, {
          ...sizeConfig, productUrl, signal: controller.signal
        });
      }
      content.appendChild(inner);
      dialog.setAttribute('aria-label', title?.textContent.trim() || current.title);
      window.theme?.wrapElements?.(inner);
      inner.querySelectorAll('product-info').forEach((info) => info.flkty?.resize());
      // Zoom is optional: its resources must never block size selection or adding to cart.
      const zoomNode = html.querySelector('[data-lounelle-upsell-zoom-config]');
      if (zoomNode) {
        const zoomConfig = JSON.parse(zoomNode.textContent);
        const zoomResources = [loadResource(zoomConfig.script, 'script'), loadResource(zoomConfig.css, 'style')];
        if (!customElements.get('zoom-images')) zoomResources.push(loadResource(zoomConfig.engine, 'script'));
        Promise.all(zoomResources).then(() => {
          if (session !== current || controller.signal.aborted || !inner.isConnected) return;
          zoomCleanup = window.LounelleCartUpsellZoom.mount(inner, {
            ...zoomConfig,
            template: html.querySelector('template[data-lounelle-upsell-zoom-template]'),
            closeLabel: current.closeLabel,
            errorLabel: current.errorLabel
          });
        }).catch((error) => console.warn('[Lounelle cart image zoom]', error));
      }
    } catch (error) {
      if (session === current && current.controller === controller) {
        console.warn('[Lounelle cart popup]', error);
        showError(current.errorLabel);
      }
    } finally {
      clearTimeout(timer);
      if (session === current && current.controller === controller) {
        current.loading = false;
        dialog.removeAttribute('aria-busy');
        content.inert = false;
      }
    }
  }

  async function addProduct(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement) || !form.matches('[data-product-form]')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    const current = session;
    const info = form.closest('product-info');
    const button = event.submitter || form.querySelector('[type="submit"]');
    if (!current || current.adding || current.loading || button?.disabled || info?.pendingRequestUrl) return;
    const variant = info?.getSelectedVariant?.(info);
    const id = form.elements.namedItem('id')?.value;
    if (!id || (variant && (!variant.available || String(variant.id) !== id))) return;
    const data = new FormData(form);
    data.set('sections', 'api-cart-items');
    data.set('sections_url', `${rootPath()}cart`);
    current.adding = true;
    content.inert = true;
    closeButton.disabled = true;
    button?.classList.add('is-loading');
    dialog.setAttribute('aria-busy', 'true');
    status.hidden = true;
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 20000);
    try {
      const response = await fetch(`${rootPath()}cart/add.js`, {
        method: 'POST', credentials: 'same-origin', headers: { Accept: 'application/json' }, body: data, signal: controller.signal
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(typeof result.description === 'string' ? result.description : current.errorLabel);
      try {
        const markup = result.sections?.['api-cart-items'];
        const api = markup && new DOMParser().parseFromString(markup, 'text/html').querySelector('[data-api-content]');
        if (!api || typeof current.cart.build !== 'function') throw new Error('Cart section unavailable');
        current.cart.build(api);
      } catch (_) {
        document.dispatchEvent(new CustomEvent('theme:cart:refresh', { bubbles: true }));
      }
      current.adding = false;
      dialog.close('added');
    } catch (error) {
      console.warn('[Lounelle cart popup]', error);
      showError(error.name === 'AbortError' || error instanceof TypeError ? current.errorLabel : error.message);
    } finally {
      clearTimeout(timer);
      current.adding = false;
      button?.classList.remove('is-loading');
      closeButton.disabled = false;
      content.inert = false;
      dialog.removeAttribute('aria-busy');
    }
  }

  async function openModal(trigger) {
    if (session || opening || trigger.hasAttribute('aria-busy')) return;
    const section = trigger.closest('.lounelle-cart-bestsellers');
    const cart = trigger.closest('[data-lounelle-native-cart]');
    if (!section || !cart) return;
    opening = true;
    trigger.setAttribute('aria-busy', 'true');
    trigger.classList.add('is-loading');
    try {
      await loadResource(section.dataset.modalCss, 'style');
      const labels = {
        title: trigger.getAttribute('aria-label') || trigger.textContent.trim(),
        closeLabel: section.dataset.modalClose,
        errorLabel: section.dataset.modalError
      };
      ensureDialog(labels);
      session = {
        ...labels, trigger, cart, adding: false, loading: false,
        scroll: Array.from(cart.querySelectorAll('[data-cart-drawer-body], [data-lounelle-upsell-desktop-slot], .lounelle-cart-bestsellers__track')).map((node) => [node, node.scrollTop, node.scrollLeft])
      };
      window.theme?.a11y?.removeTrapFocus();
      document.documentElement.classList.add('lounelle-upsell-dialog-open');
      dialog.showModal();
      loadProduct(trigger.href);
    } catch (error) {
      console.warn('[Lounelle cart popup]', error);
      if (session) { dialog.close(); restoreCart(); }
      const message = document.createElement('p');
      message.setAttribute('role', 'alert');
      message.textContent = section.dataset.modalError;
      trigger.closest('article')?.appendChild(message);
    } finally {
      opening = false;
      trigger.removeAttribute('aria-busy');
      trigger.classList.remove('is-loading');
    }
  }

  document.addEventListener('click', (event) => {
    const trigger = event.target instanceof Element ? event.target.closest('[data-lounelle-cart-upsell-quick-view]') : null;
    if (!trigger) return;
    event.preventDefault();
    event.stopImmediatePropagation();
    openModal(trigger);
  }, true);

  function placeUpsells() {
    const root = document.querySelector('[data-lounelle-native-cart]');
    if (!root) return;
    const holder = root.querySelector('[data-pair-products-holder]');
    const desktopSlot = root.querySelector('[data-lounelle-upsell-desktop-slot]');
    const mobileSlot = root.querySelector('[data-lounelle-upsell-mobile-slot]');
    if (!holder || !desktopSlot || !mobileSlot) return;
    // Keep the one native AJAX holder: mobile order is articles, upsells, price recap.
    if (!desktopMedia.matches) {
      const prices = root.querySelector('[data-cart-price-holder]');
      const widgets = mobileSlot.closest('.cart__widgets') || mobileSlot;
      if (prices && widgets.nextElementSibling !== prices) prices.before(widgets);
    }
    const target = desktopMedia.matches ? desktopSlot : mobileSlot;
    if (holder.parentElement !== target) target.appendChild(holder);
    desktopSlot.classList.toggle('is-empty', !holder.querySelector('.lounelle-cart-bestsellers, [data-pair-products]'));
    const css = holder.querySelector('[data-modal-css]')?.dataset.modalCss;
    if (css) loadResource(css, 'style').catch(() => {});
  }

  ['theme:cart:loaded', 'theme:cart:open', 'theme:cart:added', 'theme:cart:change', 'theme:cart:update'].forEach((name) => {
    document.addEventListener(name, () => requestAnimationFrame(placeUpsells));
  });
  desktopMedia.addEventListener('change', placeUpsells);
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', placeUpsells, { once: true });
  else placeUpsells();
})();
